import { logAutoCompleteSuggestionAcceptance } from "@/components/FullPageEditor/components/menus/GhostTextMenu/utils";
import FlashcardEditor from "@/components/flashcards/flashcardsCard/EditableFlashcardCard/FlashcardEditor";
import { useChatEditorCommunicator } from "@/features/AI/KaiChat/hooks/useChatEditorCommunicator";
import { ChatFlowState } from "@/features/AI/KaiChat/types";
import { useSetEditingContextSelector } from "@/features/FlashcardsEditingPage/hooks/useSetEditingContext";
import { FlashcardSortable } from "@/features/FlashcardsEditingPage/types";
import { useSafeQueryNavigation } from "@/hooks/navigation/useSafeQueryNavigation";
import { Editor } from "@knowt/editor/react";
import { AICompletionType, FlashcardSide, ItemType, Language } from "@knowt/syncing/graphql/schema";
import { FlashcardAutocompleteType } from "@knowt/syncing/hooks/ai/constants";
import { getAIFlashcardsContext, useAICopilot } from "@knowt/syncing/hooks/ai/useAICopilot";
import { FLASHCARD_LANGUAGES } from "@knowt/syncing/hooks/flashcards/languageUtils";
import { UseFlashcardSet } from "@knowt/syncing/hooks/flashcards/useFlashcards";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { ReactNode } from "react";
import { isFlashcardCardEmpty, mergeTermAndDefinitionText } from "./utils";

type TermAndDefinitionInputsProps = {
    updateFlashcardSetMetaData?: UseFlashcardSet["updateFlashcardSetMetaData"];
    flashcard: FlashcardSortable;
    termLanguage?: Language | null;
    definitionLanguage?: Language | null;
    initialTerm: string;
    initialDefinition: string;
    handleUpdateTerm: (term: string) => void;
    handleUpdateDefinition: (definition: string) => void;
    handleUpdateImage: (image: string) => void;
    handleUpdateSecondaryImage: (image: string) => void;
    renderTermSection: (c: ReactNode) => ReactNode;
    renderDefinitionSection: (c: ReactNode) => ReactNode;
    setCurrInputFocus: (props: { term?: boolean; definition?: boolean }) => void;
    currInputFocus: { term?: boolean; definition?: boolean };
};

type FlashcardEditorsRef = {
    termEditor: Editor | null;
    definitionEditor: Editor | null;
};

const TermAndDefinitionInputs = ({
    ref,
    flashcard,
    updateFlashcardSetMetaData,
    termLanguage: termLanguage_ = null,
    definitionLanguage: definitionLanguage_ = null,
    initialTerm,
    initialDefinition,
    handleUpdateTerm,
    handleUpdateDefinition,
    handleUpdateImage,
    handleUpdateSecondaryImage,
    renderTermSection,
    renderDefinitionSection,
    setCurrInputFocus,
    currInputFocus,
}: TermAndDefinitionInputsProps & {
    ref: React.RefObject<FlashcardEditorsRef>;
}) => {
    const { user } = useCurrentUser();

    const setIsChatSplitSectionOpen = useSetEditingContextSelector(s => s.setIsChatSplitSectionOpen);
    const setIsCopilotSettingsOpen = useSetEditingContextSelector(s => s.setIsCopilotSettingsOpen);
    const flashcardSet = useSetEditingContextSelector(s => s.flashcardSet);

    const termLanguage = termLanguage_ ?? FLASHCARD_LANGUAGES.ENGLISH;
    const defLanguage = definitionLanguage_ ?? FLASHCARD_LANGUAGES.ENGLISH;

    const setIsEmptyFlashcardSelected = useChatEditorCommunicator(s => s.setIsEmptyFlashcardSelected);
    const setSelectedContent = useChatEditorCommunicator(s => s.setSelectedContent);
    const setFlashcardEditor = useChatEditorCommunicator(s => s.setFlashcardEditor);
    const handleUnselect = useChatEditorCommunicator(s => s.handleUnselect);
    const setChatFlowState = useChatEditorCommunicator(s => s.setChatFlowState);
    const focusToChatInput = useChatEditorCommunicator(s => s.focusToChatInput);

    const { addParamsSilentReplace } = useSafeQueryNavigation();

    const handleFocusForAI = ({ editor: _editor }: { editor?: Editor }) => {
        handleUnselect();

        const termText = ref.current.termEditor?.getHTML() || "";
        const definitionText = ref.current.definitionEditor?.getHTML() || "";
        const fIdx = flashcard.index;

        setSelectedContent(mergeTermAndDefinitionText({ term: termText, definition: definitionText, index: fIdx }));
        setIsEmptyFlashcardSelected(isFlashcardCardEmpty({ term: termText, definition: definitionText }));
        setChatFlowState(ChatFlowState.SELECTED_TEXT_ACTIONS);

        setFlashcardEditor({
            term: ref.current.termEditor,
            definition: ref.current.definitionEditor,
            flashcardId: flashcard.flashcardId,
        });
    };

    const { getSuggestion } = useAICopilot({
        itemType: ItemType.FLASHCARDSET,
        itemId: flashcardSet?.flashcardSetId as string,
    });

    return (
        <>
            {renderTermSection(
                <FlashcardEditor
                    label="Term"
                    defaultValue={initialTerm}
                    handleInputChange={s => {
                        handleUpdateTerm(s);
                    }}
                    accentProps={{
                        language: termLanguage,
                        onLanguageSelected: lang => {
                            updateFlashcardSetMetaData?.({ termLanguage: lang });
                        },
                        isAccentButtonsVisible: currInputFocus.term,
                        isClickableLanguageVisible: currInputFocus.term || currInputFocus.definition,
                    }}
                    flashcard={{
                        side: FlashcardSide.TERM,
                        term: { content: initialTerm, language: termLanguage },
                        definition: { content: initialDefinition, language: defLanguage },
                        onChooseLanguage: selectedLang =>
                            updateFlashcardSetMetaData?.({ definitionLanguage: selectedLang }),
                    }}
                    editorProps={{
                        disableCopilot: true,
                        onCreate: ({ editor }) => {
                            ref.current.termEditor = editor;
                        },
                        onFocus: ({ editor }) => {
                            handleFocusForAI({ editor });
                            setCurrInputFocus({ term: true });
                        },
                        onBlur: () => {
                            setCurrInputFocus({ term: false });
                            ref.current.termEditor?.commands.clearSuggestion?.();
                        },
                        onAITrigger: ({ editor }) => {
                            handleFocusForAI({ editor });
                            setIsChatSplitSectionOpen?.(true);
                            addParamsSilentReplace({ tab: "Chat" });
                            focusToChatInput();
                        },
                    }}
                    handleImagePaste={handleUpdateSecondaryImage}
                    setIsCopilotSettingsOpen={setIsCopilotSettingsOpen}
                />
            )}

            {renderDefinitionSection(
                <FlashcardEditor
                    label="Definition"
                    defaultValue={initialDefinition}
                    handleInputChange={handleUpdateDefinition}
                    accentProps={{
                        language: defLanguage,
                        onLanguageSelected: lang => updateFlashcardSetMetaData?.({ definitionLanguage: lang }),
                        isAccentButtonsVisible: currInputFocus.definition,
                        isClickableLanguageVisible: currInputFocus.term || currInputFocus.definition,
                    }}
                    flashcard={{
                        side: FlashcardSide.DEFINITION,
                        term: { content: initialTerm, language: termLanguage },
                        definition: { content: initialDefinition, language: defLanguage },
                        onChooseLanguage: selectedLang =>
                            updateFlashcardSetMetaData?.({ definitionLanguage: selectedLang }),
                    }}
                    editorProps={{
                        disableCopilot: flashcardSet?.autocompleteSettings
                            ? !!flashcardSet?.autocompleteSettings?.disabled
                            : !!user?.autocompleteSettings?.disabled,

                        timeout: flashcardSet?.autocompleteSettings?.speed || user?.autocompleteSettings?.speed || 2000,

                        onCreate: ({ editor }) => {
                            ref.current.definitionEditor = editor;
                        },
                        onFocus: async ({ editor }) => {
                            handleFocusForAI({ editor });
                            setCurrInputFocus({ definition: true });
                            const text = editor.getText();
                            if (
                                !text && flashcardSet?.autocompleteSettings
                                    ? !flashcardSet?.autocompleteSettings?.disabled
                                    : !user?.autocompleteSettings?.disabled
                            ) {
                                ref.current.definitionEditor?.commands?.populateSuggestion?.({
                                    alternate: false,
                                });
                            }
                        },
                        onBlur: () => {
                            setCurrInputFocus({ definition: false });
                            ref.current.definitionEditor?.commands.clearSuggestion?.();
                        },
                        onAITrigger: ({ editor }) => {
                            handleFocusForAI({ editor });
                            setIsChatSplitSectionOpen?.(true);
                            addParamsSilentReplace({ tab: "Chat" });
                            focusToChatInput();
                        },
                        onSuggestionFetch: async () => {
                            const term = ref.current.termEditor?.getText() || "";
                            const definition = ref.current.definitionEditor?.getText() || "";
                            if (!term && !definition) return "";

                            const { flashcards, title } = await getAIFlashcardsContext({
                                flashcardSetId: flashcard.flashcardSetId as string,
                                currFlashcardIdx: flashcard.index,
                            });

                            const suggestion = await getSuggestion({
                                type: AICompletionType.FLASHCARD_AUTOCOMPLETE,
                                aiParams: {
                                    type: !definition
                                        ? FlashcardAutocompleteType.AUTOCOMPLETE_DEFINITION_FROM_TERM
                                        : FlashcardAutocompleteType.AUTOCOMPLETE_DEFINITION,
                                    context: flashcards,
                                    topic: title,
                                    term,
                                    definition,
                                    messages: [],
                                },
                            });

                            return suggestion;
                        },
                        onAlternateSuggestionFetch: async () => {
                            const term = ref.current.termEditor?.getText() || "";
                            const definition = ref.current.definitionEditor?.getText() || "";

                            if (!term && !definition) return "";

                            const { flashcards, title } = await getAIFlashcardsContext({
                                flashcardSetId: flashcard.flashcardSetId as string,
                                currFlashcardIdx: flashcard.index,
                            });

                            const suggestion = await getSuggestion({
                                type: AICompletionType.FLASHCARD_AUTOCOMPLETE,
                                aiParams: {
                                    type: !definition
                                        ? FlashcardAutocompleteType.REVISION_DEFINITION_FROM_TERM
                                        : FlashcardAutocompleteType.REVISION_DEFINITION,
                                    context: flashcards,
                                    topic: title,
                                    term,
                                    definition,
                                    messages: [],
                                },
                            });

                            return suggestion;
                        },
                        onSuggestionAccept: ({ state }) => {
                            logAutoCompleteSuggestionAcceptance({
                                isAccepted: state,
                            });
                        },
                    }}
                    handleImagePaste={handleUpdateImage}
                    setIsCopilotSettingsOpen={setIsCopilotSettingsOpen}
                />
            )}
        </>
    );
};

export default TermAndDefinitionInputs;
